import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { useState } from "react";

import { Badge } from "common/core/badge";
import AlertMessage from "common/core/alert_message";
import { SettingProfileEnum } from "graphql_globals";
import { capitalizeFirstLetter } from "util/string";
import { IconButton } from "common/core/button/icon_button";
import PopoutMenuItem from "common/core/popout_menu/item";
import Icon from "common/core/icon";
import PopoutMenu from "common/core/popout_menu";
import { PopoutState } from "common/core/popout_menu/common";
import Button from "common/core/button";

import { DeleteProfileModal } from "../../common/delete_profile/delete_profile_modal";
import { ToggleDefaultProfileModal } from "./set_default_brand_profile";
import { type BrandProfile_organization_Organization_settingProfiles as ProfileType } from "../graphql/brand_profiles.query.graphql";
import Styles from "./index.module.scss";

const MESSAGES = defineMessages({
  delete: {
    id: "52a4bbcf-2f78-4fbc-9b07-726a134ecc19",
    defaultMessage: "Delete profile",
  },
  setAsDefault: {
    id: "ea7667e4-98c9-4e4e-93cf-92c9e1331956",
    defaultMessage: "Set as default",
  },
  removeAsDefault: {
    id: "a72725d6-62f0-4c32-88d8-eb1aa07db5eb",
    defaultMessage: "Remove as default",
  },
  actions: {
    id: "a3c579be-aaf5-4ca9-ad25-52617df3d51c",
    defaultMessage: "Actions",
  },
});

export function BrandProfileBadge({
  brandProfileId,
  numOrgsUsing,
  totalOrgs,
}: {
  brandProfileId: string;
  numOrgsUsing: number;
  totalOrgs: number;
}) {
  if (numOrgsUsing === totalOrgs) {
    return (
      <Badge
        kind="success"
        withIcon="check-circle"
        data-automation-id={`bsp-${brandProfileId}-badge`}
        className={Styles.profileBadge}
      >
        <FormattedMessage
          id="65864d09-dca8-4202-b326-483e29ded59f"
          defaultMessage="In use (all organizations)"
        />
      </Badge>
    );
  } else if (numOrgsUsing > 0) {
    return (
      <Badge
        kind="success"
        withIcon="check-circle"
        data-automation-id={`bsp-${brandProfileId}-badge`}
        className={Styles.profileBadge}
      >
        <FormattedMessage
          id="d4c6281e-5a53-4dbf-9ed0-e09bf9a139c6"
          defaultMessage="In use ({orgsCount} {orgsCount, plural, one {organization} other {organizations}})"
          values={{ orgsCount: numOrgsUsing }}
        />
      </Badge>
    );
  }
  return (
    <Badge
      kind="infoBlue"
      withIcon="info-circle"
      data-automation-id={`bsp-${brandProfileId}-badge`}
      className={Styles.profileBadge}
    >
      <FormattedMessage id="e817f800-8b45-4cdb-a777-f077575bd980" defaultMessage="Not in use" />
    </Badge>
  );
}

export function DefaultProfileBadge() {
  return (
    <Badge kind="infoBlue" withIcon="star" className={Styles.profileBadge}>
      <FormattedMessage id="11b0f554-5edc-4482-8277-546ac737a713" defaultMessage="Default" />
    </Badge>
  );
}

export function SettingsRestrictedBanner({
  showBanner,
  profileType,
}: {
  showBanner: boolean;
  profileType: SettingProfileEnum;
}) {
  if (!showBanner) {
    return null;
  }
  return (
    <AlertMessage kind="info" className={Styles.restrictedBanner}>
      <FormattedMessage
        id="e817f800-8b45-4cdb-a777-f077575bd980"
        defaultMessage="Your organization is using a {profileType} profile set by a company administrator. You cannot edit these settings. Contact your administrator to make changes."
        values={{ profileType: capitalizeFirstLetter(profileType.toLowerCase()) }}
      />
    </AlertMessage>
  );
}

export enum SettingProfileOptionMenuType {
  KEBAB,
  ACTION,
}

export function SettingProfileOptionsMenu({
  menuType,
  profile,
  defaultProfile,
}: {
  menuType: SettingProfileOptionMenuType;
  profile: ProfileType;
  defaultProfile: ProfileType | null;
}) {
  const intl = useIntl();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSetDefaultModal, setShowSetDefaultModal] = useState(false);
  const [popoutState, setPopoutState] = useState<PopoutState>(PopoutState.CLOSED);
  const iconName = popoutState === PopoutState.OPEN ? "caret-up" : "caret-down";
  return (
    <>
      <PopoutMenu
        placement="bottomRight"
        target={
          menuType === SettingProfileOptionMenuType.KEBAB ? (
            <IconButton label="kebab" name="kebab" variant="secondary" buttonColor={"action"} />
          ) : (
            <Button
              variant="secondary"
              buttonColor="action"
              withIcon={{
                name: iconName,
                placement: "right",
                size: "large",
              }}
            >
              <>{intl.formatMessage(MESSAGES.actions)}</>
            </Button>
          )
        }
        onStateChange={(state) => setPopoutState(state)}
      >
        {({ close }) => (
          <>
            <PopoutMenuItem
              onClick={() => {
                close();
                setShowSetDefaultModal(true);
              }}
            >
              <Icon
                className={Styles.starIcon}
                name={profile.isDefault ? "x-mark-circle" : "star"}
                size={"large"}
              />
              {profile.isDefault
                ? intl.formatMessage(MESSAGES.removeAsDefault)
                : intl.formatMessage(MESSAGES.setAsDefault)}
            </PopoutMenuItem>
            <PopoutMenuItem
              onClick={() => {
                close();
                setShowDeleteModal(true);
              }}
            >
              <Icon className={Styles.trashIcon} name="trash" size={"large"} />
              {intl.formatMessage(MESSAGES.delete)}
            </PopoutMenuItem>
          </>
        )}
      </PopoutMenu>

      {showDeleteModal && (
        <DeleteProfileModal profile={profile} setShowDeleteModal={setShowDeleteModal} />
      )}
      {showSetDefaultModal && (
        <ToggleDefaultProfileModal
          profileToToggle={profile}
          currentProfile={defaultProfile}
          profileTypeEnum={SettingProfileEnum.BRAND}
          onModalClose={() => {
            setShowSetDefaultModal(false);
          }}
        />
      )}
    </>
  );
}
